import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import CraftSection from './components/CraftSection';
import CollectionSection from './components/CollectionSection';
import CTASection from './components/CTASection';
import Scroller from './components/Scroller';

const App = () => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [isMoving, setIsMoving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [touchEnabled, setTouchEnabled] = useState(true);
  const wrapperRef = useRef(null);
  const sections = [0, 1, 2, 3];

  const goTo = (idx) => {
    if (isMoving) return;
    setIsMoving(true);
    setCurrentIdx(idx);

    if (wrapperRef.current) {
      wrapperRef.current.style.transform = `translateY(-${currentIdx * 100}vh)`;
    }

    setTimeout(() => {
      setIsMoving(false);
    }, 1100);
  };

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.deltaY > 0 && currentIdx < sections.length - 1) {
        goTo(currentIdx + 1);
      } else if (e.deltaY < 0 && currentIdx > 0) {
        goTo(currentIdx - 1);
      }
    };

    let touchStart = 0;
    let touchEnd = 0;
    let touchStartTime = 0;
    let isScrolling = false;
    
    const handleTouchStart = (e) => {
      touchStart = e.touches[0].clientY;
      touchStartTime = Date.now();
      isScrolling = false;
    };

    const handleTouchMove = (e) => {
      touchEnd = e.touches[0].clientY;
      const currentTouch = e.touches[0].clientY;
      const diff = Math.abs(currentTouch - touchStart);
      
      if (diff > 10) {
        isScrolling = true;
      }
    };

    const handleTouchEnd = (e) => {
      const swipeDistance = touchStart - touchEnd;
      const swipeDuration = Date.now() - touchStartTime;
      const minSwipeDistance = window.innerWidth <= 480 ? 25 : 40;
      const maxSwipeDuration = 300; // Fast swipe
      
      // Enhanced swipe detection with velocity consideration
      const swipeVelocity = Math.abs(swipeDistance) / swipeDuration;
      const minVelocity = window.innerWidth <= 480 ? 0.1 : 0.15;
      
      if (Math.abs(swipeDistance) > minSwipeDistance && 
          swipeDuration < maxSwipeDuration && 
          swipeVelocity > minVelocity &&
          isScrolling) {
        
        // Add haptic feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(10);
        }
        
        if (swipeDistance > 0 && currentIdx < sections.length - 1) {
          goTo(currentIdx + 1);
        } else if (swipeDistance < 0 && currentIdx > 0) {
          goTo(currentIdx - 1);
        }
      }
    };

    // Keyboard navigation for mobile devices
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' && currentIdx < sections.length - 1) {
        goTo(currentIdx + 1);
      } else if (e.key === 'ArrowUp' && currentIdx > 0) {
        goTo(currentIdx - 1);
      }
    };

    window.addEventListener('wheel', handleWheel);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentIdx, isMoving]);

  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.style.transform = `translateY(-${currentIdx * 100}vh)`;
    }
  }, [currentIdx]);

  // Initialize app and detect touch capabilities
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);

    // Detect touch capabilities
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    setTouchEnabled(hasTouch);

    return () => clearTimeout(timer);
  }, []);

  // Loading screen
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--bg)',
        fontFamily: "'Plus Jakarta Sans', sans-serif"
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '2px solid var(--accent)',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: 'var(--text)', fontSize: '0.9rem' }}>Chargement...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <Scroller currentIdx={currentIdx} goTo={goTo} />
      <div className="main-wrapper" ref={wrapperRef}>
        <div className={`section ${currentIdx === 0 ? 'active' : ''}`}>
          <HeroSection />
        </div>
        <div className={`section ${currentIdx === 1 ? 'active' : ''}`}>
          <CraftSection />
        </div>
        <div className={`section ${currentIdx === 2 ? 'active' : ''}`}>
          <CollectionSection />
        </div>
        <div className={`section ${currentIdx === 3 ? 'active' : ''}`}>
          <CTASection />
        </div>
      </div>
    </div>
  );
};

export default App;
